import type { HTMLAttributes } from "react";
import { cn } from "../../lib/cn";

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "relative overflow-hidden rounded-md bg-[var(--color-surface-2)]",
        "before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_ease-in-out_infinite]",
        "before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent",
        className,
      )}
      {...props}
    />
  );
}

export function SkeletonRows({ label = "Loading", rows = 3 }: { label?: string; rows?: number }) {
  const rowKeys = Array.from({ length: rows }, (_, index) => `skeleton-row-${index + 1}`);

  return (
    <div
      aria-busy="true"
      aria-label={label}
      className="divide-y divide-[var(--color-border)]"
      role="status"
    >
      {rowKeys.map((rowKey) => (
        <div
          className="grid gap-4 px-[var(--layout-card-pad-x)] py-4 md:grid-cols-[auto_minmax(0,1fr)_auto]"
          key={rowKey}
        >
          <Skeleton className="size-[var(--space-10)]" />
          <div className="grid min-w-0 gap-2">
            <Skeleton className="h-4 w-56 max-w-full" />
            <Skeleton className="h-3 w-80 max-w-full" />
            <Skeleton className="h-3 w-32 max-w-full" />
          </div>
          <div className="flex gap-2 md:justify-end">
            <Skeleton className="h-[var(--icon-button-sm)] w-20" />
            <Skeleton className="size-[var(--icon-button-md)]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton({
  className,
  label,
  panels = 1,
}: {
  className?: string;
  label: string;
  panels?: number;
}) {
  const panelKeys = Array.from({ length: panels }, (_, index) => `skeleton-panel-${index + 1}`);

  return (
    <div
      aria-busy="true"
      aria-label={label}
      className={cn("grid gap-[var(--layout-section-gap)]", className)}
      role="status"
    >
      <div className="grid gap-[var(--space-2)]">
        <Skeleton className="h-[var(--space-3)] w-[var(--space-16)]" />
        <Skeleton className="h-[var(--space-8)] w-full max-w-[calc(var(--space-16)*4)]" />
        <Skeleton className="h-[var(--space-4)] w-full max-w-[calc(var(--space-16)*6)]" />
      </div>
      {panelKeys.map((panelKey) => (
        <div
          className="grid gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]"
          key={panelKey}
        >
          <Skeleton className="h-[var(--space-5)] w-full max-w-[calc(var(--space-16)*3)]" />
          <Skeleton className="h-[var(--space-3)] w-full" />
          <Skeleton className="h-[var(--space-3)] w-full max-w-[calc(var(--space-16)*5)]" />
          <Skeleton className="h-[calc(var(--space-16)*4)] w-full" />
        </div>
      ))}
    </div>
  );
}

export function FormSkeleton({
  className,
  fields = 4,
  label,
}: {
  className?: string;
  fields?: number;
  label: string;
}) {
  const fieldKeys = Array.from({ length: fields }, (_, index) => `skeleton-field-${index + 1}`);

  return (
    <div
      aria-busy="true"
      aria-label={label}
      className={cn(
        "grid gap-[var(--space-5)] rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]",
        className,
      )}
      role="status"
    >
      {fieldKeys.map((fieldKey) => (
        <div className="grid gap-[var(--space-2)]" key={fieldKey}>
          <Skeleton className="h-[var(--space-3)] w-[calc(var(--space-16)*2)] max-w-full" />
          <Skeleton className="h-[var(--field-h)] w-full" />
        </div>
      ))}
      <div className="flex justify-end gap-[var(--space-2)]">
        <Skeleton className="h-[var(--control-h-md)] w-[calc(var(--space-16)*2)]" />
        <Skeleton className="h-[var(--control-h-md)] w-[calc(var(--space-16)*2)]" />
      </div>
    </div>
  );
}
